import { Auth } from 'aws-amplify';
import { ConfigurationManager } from 'shell/utils/config';
import { createHttpClient } from 'utils';
import { logoutUrl } from 'constants';
import { store } from '../store';
import appEventBus, { API_ADD_ACTIVE_AD_GROUP_HEADER, API_REMOVE_ACTIVE_AD_GROUP_HEADER } from './appEventBus';

let config = new ConfigurationManager();
let logger = { error: () => null };

const loggerResolver = () => logger;

const httpClient = createHttpClient(store, loggerResolver);
const globalHttpHeaders = new Map();

let mockApiService;

export const cancelRequest = { cancel: () => null };

if (process.env.NODE_ENV === 'development') {
  const { MockApiService } = require('./MockApiService'); // eslint-disable-line global-require
  mockApiService = MockApiService.createMockApiService();
}

/**
 *  Create API service object with next interface
 *  - get (uri, options): Promise
 *  - post (uri, data, options): Promise
 *  - delete (uri, options): Promise
 *
 *  options - represents next data:
 *  - headers - key/value http headers
 *  - params - URL query parameters
 *  - clientId - shortcut for `headers: { 'X-Customer-Id': <value> }`
 *  - httpClientOptions - options specific to httpClient instance
 *
 * @param {Function} endpointResolver - function which should return endpoint base URL
 * @returns {Object}
 */
const createApiService = (endpointResolver) => {
  return ['get', 'post', 'put', 'delete'].reduce((res, method) => {
    res[method] = async (uri, ...args) => {
      // use mock service in case it's available and enabled
      if (mockApiService && mockApiService.isMockAvailableAndEnabled(method, endpointResolver(), uri)) {
        const [options = {}] = args;
        return mockApiService.exec(method, endpointResolver(), uri, { params: options.params });
      }
      let bearerToken;
      try {
        // Get auth JWT token
        bearerToken = (await Auth.currentSession()).getIdToken().getJwtToken();
      } catch (e) {
        if (e.message === 'Refresh Token has expired') {
          window.location.href = logoutUrl(config.get('Auth'));
          return;
        }
        logger.error(e);
        throw e;
      }

      const requestArgs = [uri];
      let postBody;
      let options;

      if (['post', 'put'].includes(method)) {
        [postBody = null, options = {}] = args;
        requestArgs.push(postBody);
      } else {
        [options = {}] = args;
      }

      const { headers, params, httpClientOptions, clientId, data, cancelable } = options;

      // build HTTP headers
      const preparedHeaders = {
        Authorization: `Bearer ${bearerToken}`,
        ...(clientId && { 'X-Customer-Id': clientId }),
        ...headers,
      };

      if (globalHttpHeaders.size > 0) {
        globalHttpHeaders.forEach((headerValue, headerName) => {
          preparedHeaders[headerName] = headerValue;
        });
      }

      const requestOptions = {
        baseURL: endpointResolver(),
        headers: preparedHeaders,
        cancelToken:
          cancelable &&
          new httpClient.CancelToken((c) => {
            cancelRequest.cancel = c;
          }),
        params,
        data,
      };

      requestArgs.push(requestOptions);

      return httpClient(httpClientOptions)[method](...requestArgs);
    };

    return res;
  }, {});
};

// TODO: Should be found much better solution instead of just exporting function below
export const updateApiConfigAndLogger = (newConfig, newLogger) => {
  config = newConfig;
  logger = newLogger;

  if (mockApiService) {
    mockApiService.buildMockRouting(config);
  }
};
// TODO: End

appEventBus.on(API_ADD_ACTIVE_AD_GROUP_HEADER, (value) => globalHttpHeaders.set('X-NMG-Group', value));
appEventBus.on(API_REMOVE_ACTIVE_AD_GROUP_HEADER, () => globalHttpHeaders.delete('X-NMG-Group'));

export const clientsService = createApiService(() => config.get('api.endpoints.clients'));
export const clientsCentralSearchService = createApiService(() => config.get('api.endpoints.clientsCentralSearch'));
export const clientsCentralSearchServiceV2 = createApiService(() => config.get('api.endpoints.clientsCentralSearchV2'));
export const profileService = createApiService(() => config.get('api.endpoints.profile'));
export const cmdProfileService = createApiService(() => config.get('api.endpoints.cmdProfile'));
export const cmdProfileServiceV3 = createApiService(() => config.get('api.endpoints.cmdProfileV3'));
export const cartService = createApiService(() => config.get('api.endpoints.cart'));
export const paymentService = createApiService(() => config.get('api.endpoints.paymentIframe'));
export const orderService = createApiService(() => config.get('api.endpoints.order'));
export const orderHistoryService = createApiService(() => config.get('api.endpoints.orderHistory'));
export const associateHistoryService = createApiService(() => config.get('api.endpoints.associateOrderHistory'));
export const addressValidationService = createApiService(() => config.get('api.endpoints.addressValidation'));
export const associatePerformanceService = createApiService(() => config.get('api.endpoints.associatePerformance'));
export const associateScorecardService = createApiService(() => config.get('api.endpoints.associateScorecard'));
export const groupsService = createApiService(() => config.get('api.endpoints.groups'));
export const groupsV2Service = createApiService(() => config.get('api.endpoints.groupsV2'));
export const conversationService = createApiService(() => config.get('api.endpoints.conversations'));
export const conversationServiceV2 = createApiService(() => config.get('api.endpoints.conversationsV2'));
export const attachmentsService = createApiService(() => config.get('api.endpoints.getUploadUrls'));
export const eventDetailsService = createApiService(() => config.get('api.endpoints.eventDetails'));
export const productServiceWithInHouseData = createApiService(() => config.get('api.endpoints.productFromInHouseData'));
export const productDetailsService = createApiService(() => config.get('api.endpoints.productDetails'));
export const productCatalogService = createApiService(() => config.get('api.endpoints.productCatalog'));
export const notifyMeService = createApiService(() => config.get('api.endpoints.notifyMe'));
export const voiceCallService = createApiService(() => config.get('twilio.tokens.voiceCall'));
export const storeService = createApiService(() => config.get('api.endpoints.storeInfo'));
export const paginatedOrderHistoryService = createApiService(() => config.get('api.endpoints.paginatedOrderHistory'));
export const skuTrackerService = createApiService(() => config.get('api.endpoints.skuTrackers'));
export const marketingAssetsService = createApiService(() => config.get('api.endpoints.marketingAssets'));
export const marketingScriptsService = createApiService(() => config.get('api.endpoints.marketingScripts'));
export const emailTemplatesService = createApiService(() => config.get('api.endpoints.emailTemplates'));
export const emailSignatureService = createApiService(() => config.get('api.endpoints.emailSignature'));
export const shortenedProductUrlService = createApiService(() => config.get('urlShortener.url'));
export const remindersService = createApiService(() => config.get('api.endpoints.reminders'));
export const alterationsService = createApiService(() => config.get('api.endpoints.alterations'));
